import { Typography } from '@mui/material';
import React, { FC, memo, useEffect, useRef } from 'react';

type DotLoaderProps = {
  tickMillis?: number;
  initialDots?: 0 | 1 | 2 | 3;
};
/**
 * This component is used to show a loading indicator with dots.
 *
 * It renders span elements with text dots inside so it can safely inherit text styling applied to the parent element.
 */
export const DotLoader: FC<DotLoaderProps> = memo(
  ({ tickMillis = 500, initialDots = 3 }) => {
    /** Implementation notes:
     * This component is used to show a loading indicator with dots.
     *
     * it updates the dots outside of react's render cycle, because we've had issues with this component re-rendering triggers
     * invalidation and re-renders of other JSX tree elements, which has caused other very annoying problems.
     * This component should be SAFE TO USE, so we detach it from react's render cycle since it's fairly simple.
     *
     * (the problem we had was rendering it in a suspense fallback, which triggered a re-render of the suspended component. The suspended component was heavy, and the dots were re-rendering, which triggered a re-render of the suspended component again, which triggered a re-render of the suspended component again resulting in a super slow render loop)
     */

    const visibleRef = useRef<HTMLSpanElement | null>(null);
    const hiddenRef = useRef<HTMLSpanElement | null>(null);
    useEffect(() => {
      let tick = initialDots;
      const updateDots = (currentTick: number) => {
        const visibleElement = visibleRef.current;
        const hiddenElement = hiddenRef.current;
        if (visibleElement) visibleElement.innerHTML = '.'.repeat(currentTick);
        if (hiddenElement)
          hiddenElement.innerHTML = '.'.repeat(3 - currentTick);
      };
      updateDots(tick);
      const interval = setInterval(() => {
        tick = (tick + 1) % 4;
        updateDots(tick);
      }, tickMillis);
      return () => clearInterval(interval);
    }, [tickMillis, initialDots]);
    return (
      <span>
        <Typography
          ref={visibleRef}
          component="span"
          variant="inherit"
          sx={{
            display: 'inline',
          }}
        ></Typography>
        <Typography
          ref={hiddenRef}
          component="span"
          variant="inherit"
          sx={{
            display: 'inline',
            visibility: 'hidden',
          }}
        ></Typography>
      </span>
    );
  },
);
